import { useEffect, useMemo, useState } from "react";
import {
  Layers3, Landmark, FileSpreadsheet, Info, Loader2, EyeOff, Check,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";

// Palet Tailwind — warna: light bg utk pill/badge readonly.
const COLOR_TONE = {
  teal: { pill: "bg-teal-50 text-teal-800 border-teal-200", dot: "bg-teal-600" },
  amber: { pill: "bg-amber-50 text-amber-800 border-amber-200", dot: "bg-amber-600" },
  pink: { pill: "bg-pink-50 text-pink-800 border-pink-200", dot: "bg-pink-600" },
  slate: { pill: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-500" },
  emerald: { pill: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-600" },
  rose: { pill: "bg-rose-50 text-rose-800 border-rose-200", dot: "bg-rose-600" },
  indigo: { pill: "bg-indigo-50 text-indigo-800 border-indigo-200", dot: "bg-indigo-600" },
  violet: { pill: "bg-violet-50 text-violet-800 border-violet-200", dot: "bg-violet-600" },
  orange: { pill: "bg-orange-50 text-orange-800 border-orange-200", dot: "bg-orange-600" },
  sky: { pill: "bg-sky-50 text-sky-800 border-sky-200", dot: "bg-sky-600" },
  gray: { pill: "bg-gray-100 text-gray-700 border-gray-200", dot: "bg-gray-500" },
};
const toneOf = (w) => COLOR_TONE[w] || COLOR_TONE.slate;

const TIPE_TONE = {
  Pendapatan: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Potongan: "text-rose-700 bg-rose-50 border-rose-200",
  "Beban Perusahaan": "text-indigo-700 bg-indigo-50 border-indigo-200",
};

function isKoperasiKomponen(nama) {
  return (nama || "").toLowerCase().includes("koperasi");
}

// Normalisasi grup_laporan_ids: prioritas field baru (list), fallback field
// lama (single) → list 1-elemen. Ini menjaga kompatibilitas selama masa
// transisi backend.
function normalizeGroupIds(item) {
  if (Array.isArray(item.grup_laporan_ids)) return item.grup_laporan_ids.filter(Boolean);
  if (item.grup_laporan_id) return [item.grup_laporan_id];
  return [];
}

export function ExportPembebananDialog({ open, onOpenChange, run, unitId }) {
  const [komponen, setKomponen] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [splitKoperasi, setSplitKoperasi] = useState(true);
  const [savingId, setSavingId] = useState(null);

  async function loadData() {
    if (!open) return;
    setLoading(true);
    try {
      const [k, g, sys] = await Promise.all([
        api.get("/payroll/komponen"),
        api.get("/report-groups"),
        api.get("/reports/payroll/system-komponen-grup"),
      ]);
      const autos = (sys.data || []).map((a) => ({
        id: `sys:${a.grup_key}`,
        nama: a.label,
        tipe: a.tipe,
        sifat: "Otomatis Sistem",
        grup_laporan_ids: normalizeGroupIds(a),
        is_system_auto: true,
        grup_key: a.grup_key,
        source: a.source,
      }));
      const masters = (k.data || [])
        .filter((x) => x.is_active !== false)
        .map((x) => ({ ...x, grup_laporan_ids: normalizeGroupIds(x) }));
      setKomponen([...masters, ...autos]);
      setGroups(g.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    setSplitKoperasi(true);
    loadData();
    // eslint-disable-next-line
  }, [open]);

  // Bucket komponen per Grup Laporan + ember "Tidak Disertakan" (kosong).
  // Multi-Group Assignment: komponen dapat muncul di BEBERAPA bucket sekaligus.
  const buckets = useMemo(() => {
    const byId = {};
    groups.forEach((g) => { byId[g.id] = { grp: g, items: [] }; });
    const none = { grp: null, items: [] };
    for (const k of komponen) {
      const ids = k.grup_laporan_ids || [];
      if (ids.length === 0) {
        none.items.push(k);
      } else {
        ids.forEach((gid) => {
          if (byId[gid]) byId[gid].items.push(k);
        });
      }
    }
    const ordered = groups.map((g) => byId[g.id]).filter((b) => b.items.length > 0);
    ordered.push(none);
    return ordered;
  }, [komponen, groups]);

  // Jumlah komponen ter-map per grup (live).
  const groupCount = useMemo(() => {
    const c = {};
    komponen.forEach((k) => {
      (k.grup_laporan_ids || []).forEach((gid) => {
        c[gid] = (c[gid] || 0) + 1;
      });
    });
    return c;
  }, [komponen]);

  const totalAssigned = komponen.filter((k) => (k.grup_laporan_ids || []).length > 0).length;
  const totalUnassigned = komponen.length - totalAssigned;
  // Jumlah section export (kontribusi per pemetaan, bisa > totalAssigned bila
  // sebagian komponen dipetakan ke banyak grup).
  const totalSections = useMemo(
    () => komponen.reduce((acc, k) => acc + (k.grup_laporan_ids || []).length, 0),
    [komponen],
  );

  // Simpan permanen assignment grup sebuah komponen (multi-group).
  async function saveGroups(item, nextIds) {
    // Dedup + preserve order berdasar urutan `groups` (master).
    const orderMap = new Map(groups.map((g, i) => [g.id, i]));
    const cleaned = Array.from(new Set(nextIds.filter(Boolean)))
      .sort((a, b) => (orderMap.get(a) ?? 999) - (orderMap.get(b) ?? 999));
    setSavingId(item.id);
    setKomponen((prev) => prev.map((k) => (k.id === item.id ? { ...k, grup_laporan_ids: cleaned } : k)));
    try {
      const body = { grup_laporan_ids: cleaned };
      if (item.is_system_auto) {
        await api.put(`/reports/payroll/system-komponen-grup/${item.grup_key}`, body);
      } else {
        await api.put(`/payroll/komponen/${item.id}/grup`, body);
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
      loadData(); // revert dari server bila gagal
    } finally {
      setSavingId(null);
    }
  }

  function toggleGroup(item, gid, checked) {
    const cur = item.grup_laporan_ids || [];
    const next = checked ? [...cur, gid] : cur.filter((x) => x !== gid);
    saveGroups(item, next);
  }

  async function handleExport() {
    if (!run) { toast.error("Belum ada Run Payroll aktif"); return; }
    if (totalAssigned === 0) {
      toast.error("Belum ada komponen yang dipetakan ke grup mana pun");
      return;
    }
    // Multi-Group Assignment: hasilkan satu section per (komponen × grup).
    const sections = [];
    for (const k of komponen) {
      const ids = k.grup_laporan_ids || [];
      if (ids.length === 0) continue;
      for (const gid of ids) {
        if (k.is_system_auto) {
          sections.push({
            key: `sys_${k.grup_key}__${gid}`,
            label: k.nama,
            source: k.source,
            komponen_id: null,
            grup_laporan_id: gid,
            group_by: "cost_center",
            positif: true,
          });
        } else {
          const isKop = isKoperasiKomponen(k.nama) && splitKoperasi;
          sections.push({
            key: `komp_${k.id}__${gid}`,
            label: k.nama,
            source: "komponen",
            komponen_id: k.id,
            grup_laporan_id: gid,
            group_by: isKop ? "koperasi" : "cost_center",
            positif: true,
          });
        }
      }
    }

    setDownloading(true);
    try {
      const body = { run_id: run.id, sections };
      if (unitId) body.unit_id = unitId;
      const resp = await api.post("/reports/payroll/export-pembebanan", body, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RekapPembebanan_${run.periode || "payroll"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("File Rekap Pembebanan berhasil diunduh");
      onOpenChange(false);
    } catch (e) {
      if (e.response?.data instanceof Blob) {
        const txt = await e.response.data.text();
        try { toast.error(JSON.parse(txt).detail || "Gagal export"); }
        catch { toast.error(txt.slice(0, 200)); }
      } else {
        toast.error(apiErrorMessage(e));
      }
    } finally {
      setDownloading(false);
    }
  }

  // Popover multi-select untuk pemetaan grup sebuah komponen.
  function GrupMultiPopover({ k }) {
    const ids = k.grup_laporan_ids || [];
    const count = ids.length;
    const saving = savingId === k.id;
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={saving}
            className={
              "h-8 min-w-[130px] px-2.5 rounded-md border border-slate-300 bg-white " +
              "hover:bg-slate-50 text-xs flex items-center gap-1.5 disabled:opacity-60 " +
              "focus:outline-none focus:ring-2 focus:ring-teal-500/40"
            }
            data-testid={`pembebanan-assign-${k.id}`}
          >
            {saving ? (
              <span className="flex items-center gap-1 text-slate-400">
                <Loader2 size={12} className="animate-spin" /> Menyimpan…
              </span>
            ) : count === 0 ? (
              <span className="flex items-center gap-1 text-slate-500">
                <EyeOff size={12} /> Tidak Disertakan
              </span>
            ) : (
              <>
                <span className="flex items-center gap-0.5">
                  {ids.slice(0, 3).map((gid) => {
                    const g = groups.find((x) => x.id === gid);
                    if (!g) return null;
                    return (
                      <span
                        key={gid}
                        className={`w-2 h-2 rounded-full ${toneOf(g.warna).dot}`}
                        title={g.nama}
                      />
                    );
                  })}
                </span>
                <span className="font-semibold text-slate-700">
                  {count} grup
                </span>
              </>
            )}
            <ChevronDown size={12} className="ml-auto text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-1.5" align="end">
          <div className="px-2 py-1.5 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Kelola Grup Laporan
            </span>
            {count > 0 && (
              <button
                type="button"
                className="text-[10px] text-rose-600 hover:text-rose-700 font-semibold"
                onClick={() => saveGroups(k, [])}
                data-testid={`pembebanan-clear-${k.id}`}
              >
                Kosongkan
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto">
            {groups.map((g) => {
              const checked = ids.includes(g.id);
              const t = toneOf(g.warna);
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => toggleGroup(k, g.id, !checked)}
                  className={
                    "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left " +
                    "text-xs hover:bg-slate-50 focus:outline-none focus:bg-slate-100"
                  }
                  data-testid={`pembebanan-grp-toggle-${k.id}-${g.id}`}
                >
                  <span
                    className={
                      "w-4 h-4 rounded border flex items-center justify-center shrink-0 " +
                      (checked
                        ? "bg-teal-600 border-teal-600 text-white"
                        : "bg-white border-slate-300")
                    }
                  >
                    {checked && <Check size={12} strokeWidth={3} />}
                  </span>
                  <span className={`w-1.5 h-1.5 rounded-full ${t.dot} shrink-0`} />
                  <span className="flex-1 truncate">{g.nama}</span>
                </button>
              );
            })}
          </div>
          <div className="px-2 py-1.5 mt-0.5 border-t border-slate-100 text-[10px] text-slate-500 leading-relaxed">
            Komponen dapat dipetakan ke <b>beberapa grup</b>. Grand Total dihitung
            berdasar nilai asli, tidak duplikat.
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="pembebanan-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-teal-800">
            <FileSpreadsheet size={18} /> Export Rekap Pembebanan
          </DialogTitle>
          <DialogDescription>
            <b>Multi-Grup Assignment.</b> Setiap komponen bisa dipetakan ke{" "}
            <b>beberapa Grup Laporan sekaligus</b> — mis. <i>Potongan Koperasi</i>{" "}
            di grup <i>THP</i> (sebagai pengurang) sekaligus di <i>Iuran Koperasi</i>{" "}
            (sebagai tagihan). Grand Total tetap menghitung nilai asli — tidak
            ada penjumlahan ganda.
          </DialogDescription>
        </DialogHeader>

        {/* Legenda grup dinamis + jumlah ter-map */}
        {groups.length > 0 && (
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-2"
            data-testid="pembebanan-groups-legend"
          >
            {groups.map((g) => {
              const t = toneOf(g.warna);
              return (
                <div
                  key={g.id}
                  className={`rounded-lg border px-2.5 py-2 flex items-start gap-2 ${t.pill}`}
                >
                  <span className={`mt-1 w-2 h-2 rounded-full ${t.dot} shrink-0`} />
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold uppercase tracking-wider truncate">
                      {g.nama}
                    </div>
                    <div className="text-[10px] opacity-80">
                      {groupCount[g.id] || 0} komponen
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-5">
          {/* Toggle khusus koperasi */}
          <section className="flex items-center justify-between rounded-xl border border-teal-200 bg-teal-50/40 px-4 py-3">
            <div className="flex items-start gap-3">
              <Landmark size={18} className="text-teal-700 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-slate-900">
                  Pecah komponen "Koperasi" per Master Koperasi
                </div>
                <div className="text-[11px] text-slate-600 mt-0.5">
                  Bila aktif, komponen yang namanya mengandung "koperasi" dikelompokkan
                  per Master Koperasi (bukan Cost Center). Nilai selalu positif.
                </div>
              </div>
            </div>
            <Switch
              checked={splitKoperasi}
              onCheckedChange={setSplitKoperasi}
              data-testid="pembebanan-split-koperasi"
            />
          </section>

          {/* Pemetaan komponen → grup */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-widest text-teal-700 flex items-center gap-2">
                <Layers3 size={12} /> Pemetaan Komponen ke Grup Laporan
              </h4>
              <span className="text-[11px] text-slate-500">
                {loading
                  ? "Memuat…"
                  : `${totalAssigned} dipetakan · ${totalSections} pemetaan · ${totalUnassigned} belum`}
              </span>
            </div>

            {loading ? (
              <div className="text-center text-slate-400 py-6">Memuat komponen…</div>
            ) : komponen.length === 0 ? (
              <div className="text-center text-slate-400 py-6">Belum ada komponen aktif.</div>
            ) : (
              <div className="space-y-4">
                {buckets.map((bucket) => {
                  const grp = bucket.grp;
                  const items = bucket.items;
                  const isNone = !grp;
                  const t = toneOf(grp?.warna);
                  const grpKey = grp?.id || "none";
                  return (
                    <div
                      key={grpKey}
                      className={`rounded-xl border overflow-hidden ${isNone ? "border-dashed border-slate-300" : "border-slate-200"}`}
                      data-testid={`pembebanan-grp-block-${grpKey}`}
                    >
                      {/* Header grup */}
                      <div className={`flex items-center gap-2 px-3 py-2 border-b ${isNone ? "bg-slate-50 border-slate-200" : `${t.pill} border-transparent`}`}>
                        <span className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                          {isNone
                            ? <><EyeOff size={12} className="text-slate-400" /> Tidak Disertakan</>
                            : <><span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} /> {grp.nama}</>}
                        </span>
                        <span className="ml-auto text-[10px] font-semibold opacity-70">
                          {items.length} komponen
                        </span>
                      </div>

                      {/* Daftar komponen dalam grup */}
                      {items.length === 0 ? (
                        <div className="px-3 py-3 text-[11px] text-slate-400 italic bg-white">
                          Semua komponen sudah dipetakan.
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100 bg-white">
                          {items.map((k) => {
                            const isKop = isKoperasiKomponen(k.nama);
                            const tipeTone = TIPE_TONE[k.tipe] || "text-slate-600 bg-slate-50 border-slate-200";
                            const ids = k.grup_laporan_ids || [];
                            return (
                              <div
                                key={`${grpKey}-${k.id}`}
                                className="flex items-center gap-2 px-3 py-2"
                                data-testid={`pembebanan-komp-${grpKey}-${k.id}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-slate-800 flex items-center gap-1.5 flex-wrap">
                                    {k.nama}
                                    {k.is_system_auto ? (
                                      <Badge className="bg-teal-600 text-white border border-teal-700 text-[9px] font-bold uppercase">
                                        Sistem
                                      </Badge>
                                    ) : null}
                                    {isKop && splitKoperasi && !k.is_system_auto && (
                                      <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-[9px] font-bold uppercase">
                                        per Koperasi
                                      </Badge>
                                    )}
                                    {ids.length > 1 && (
                                      <Badge
                                        className="bg-violet-100 text-violet-800 border border-violet-200 text-[9px] font-bold uppercase"
                                        data-testid={`pembebanan-multi-badge-${k.id}`}
                                      >
                                        Multi ({ids.length})
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                                    <span className={`inline-flex px-1.5 py-0 rounded border text-[9px] font-bold uppercase ${tipeTone}`}>
                                      {k.tipe}
                                    </span>
                                    {k.is_system_auto ? "Hasil hitung sistem" : k.sifat}
                                  </div>
                                </div>
                                <GrupMultiPopover k={k} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-2">
            <Info size={14} className="text-slate-500 mt-0.5" />
            <div className="text-[11px] text-slate-600 leading-relaxed">
              <b>Cara pakai:</b> klik tombol grup di kanan tiap komponen — akan
              muncul popup checklist. Centang <b>satu atau lebih grup</b> untuk
              mengaitkan komponen. Perubahan langsung tersimpan. <b>Grand Total</b>{" "}
              di Excel hanya menghitung nilai asli komponen (tidak double)
              meskipun komponen tampil di beberapa section.
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-slate-200 pt-3 mt-2 flex-col sm:flex-row gap-2">
          <div className="flex-1 text-xs text-slate-600 flex items-center gap-2 flex-wrap">
            {totalAssigned > 0 ? (
              <>
                <b className="text-teal-800">{totalAssigned}</b>
                <span>komponen · </span>
                <b className="text-teal-800">{totalSections}</b>
                <span>pemetaan akan direkap</span>
                {run ? (
                  <span className="text-slate-400 ml-1">· {run.periode_label || run.periode}</span>
                ) : null}
              </>
            ) : (
              <span className="text-slate-400">Petakan minimal satu komponen ke sebuah grup…</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="pembebanan-cancel"
            >
              Tutup
            </Button>
            <Button
              className="bg-teal-700 hover:bg-teal-800 gap-2"
              disabled={downloading || totalAssigned === 0}
              onClick={handleExport}
              data-testid="pembebanan-submit"
            >
              <FileSpreadsheet size={14} />
              {downloading ? "Menyiapkan…" : "Download Excel"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ExportPembebananDialog;
